<template>
    <div>
        <div
            class="flex gap-2 items-center overflow-hidden"
            :class="{'gap-1': cardsCols >= 5}"
        >
            <redesign-base-icon
                :name="call.callType === 'Incoming' ? redesignCallTypeIcons.incoming : redesignCallTypeIcons.outgoing"
                :color="call.callType === 'Incoming' ? redesignCallTypeIcons.incomingColor: redesignCallTypeIcons.outgoingColor"
                :size="callInfoFontSize"
            />
            <div
                class="relative call-info overflow-hidden"
                :class="{
                    'flex gap-2': cardsCols !== 5,
                    'animated': shouldAnimate
                }"
                :style="callInfoStyling"
            >
                <div
                    v-show="call.callerphone"
                    class="call-info-callerphone overflow-ellipsis whitespace-nowrap overflow-hidden"
                >
                    {{ call.callerphone }}
                </div>
                <div
                    v-show="hasCallerName"
                    class="call-info-callername overflow-ellipsis whitespace-nowrap overflow-hidden"
                >
                    {{ call.callername }}
                </div>
            </div>
            <redesign-tag
                v-for="(tag, i) in tagsContent"
                :key="i + call.ivrid"
                :color="tag.color"
                :icon-size="callTagFontSize"
                :animated="tag.animated"
                :tag-content="tag.tagContent"
                :style="callTagStyling"
                class="gap-2"
            />
        </div>
        <div v-if="pauseRecording">
            <RedesignTooltip v-if="!showPauseRecTitle">
                <template #tooltip-content>
                    <div class="pause-rec-title text-nowrap mx-1 text-steel uppercase mb-1">
                        {{ $t('extensionCard.recStopped') }}
                    </div>
                </template>
                <div class="flex items-center cursor-pointer">
                    <redesign-base-icon
                        name="MicOff"
                        color="red"
                        :size="callInfoFontSize"
                    />
                </div>
            </RedesignTooltip>
            <div
                v-if="showPauseRecTitle"
                class="flex items-center"
            >
                <redesign-base-icon
                    name="MicOff"
                    color="red"
                    :size="callInfoFontSize"
                />
                <div
                    key="text"
                    class="pause-rec-title text-nowrap mx-2 text-steel uppercase"
                    :style="callTagStyling"
                >
                    {{ $t('extensionCard.recStopped') }}
                </div>
            </div>
        </div>
        <div class="hidden text-gray-600 text-main-xs relative">
            <Tooltip
                placement="top"
                effect="customized"
                popper-class="custom"
                :visible-arrow="false"
            >
                <template #content>
                    <div>
                        {{ call.ivrid }}
                    </div>
                </template>
                <div>
                    {{ 'ID' }}
                </div>
            </Tooltip>
            <!--            <div v-show="cardsCols === 4" class="animate-even absolute left-0 top-0">-->
            <!--                {{ call.callername }}-->
            <!--            </div>-->
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useTimer } from '@/composables/useTimer'
import RedesignTag from '@/components/RedesignBaseComponents/RedesignTag.vue'
import { Tooltip } from 'element-ui'
import { redesignCallTypeIcons } from '@/enum/colors'
import get from 'lodash/get'
import { realTimeSettings } from '@/enum/defaultWidgetSettings'
import { activeLayoutThresholdConfig, getSoundFromActiveLayout, isVisualAlertDisabled } from '@/composables/globalData/useLayouts'
import RedesignTooltip from '@/components/RedesignBaseComponents/RedesignTooltip.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import { timeFormatter } from '@/helpers/timeFormatter'
import i18n from '@/i18n.js'
import { removeSoundsFromQueueByIdWithHistoryKeep, setupSoundQueue } from '@/composables/useSoundsStoreData'

const TIMER_ICON = 'Timer'
const CALL_STATUS = {
    WARNING: 'warning',
    INFO: 'info'
}
const CALL_HOLD_STATUS = {
    WARNING: 'warningHold',
    INFO: 'infoHold'
}
const { timerDisplayTime, timerState, startTimer } = useTimer(false)
const {
    timerDisplayTime: timerHoldTimeDisplay,
    timerState: timerHoldState,
    startTimer: startHoldTimer,
    clearTimer: stopHoldTimer

} = useTimer(false)

/*Props*/
const props = defineProps({
    call: {
        type: Object,
        default: () => ({})
    },
    settings: {
        type: Object,
        default: () => ({})
    },
    cardsCols: Number
})

/*State*/
const callTagColor = ref('')
const callTagIcon = ref('')
const onHold = ref(false)
const holdStatus = ref('')
const activeCallStatus = ref('')

/*Computed*/
const callStartedTime = computed(() => {
    return props.call.callStarted_UTC_CLIENT
})
const thresholdConfig = computed(() => {
    return activeLayoutThresholdConfig.value
})
const callInfoFontSize = computed(() => {
    return Number(get(props.settings, 'callInfoFontSize', realTimeSettings.callInfoFontSize))
})
const callInfoStyling = computed(() => {
    return {
        fontSize: `${callInfoFontSize.value}px`,
        lineHeight: `${callInfoFontSize.value}px`
    }
})
const callTagFontSize = computed(() => {
    return callInfoFontSize.value - 4
})
const callTagStyling = computed(() => {

    return {
        fontSize: `${callTagFontSize.value}px`,
        lineHeight: `${callTagFontSize.value}px`
    }
})
const ActiveCallLimit = computed(() => thresholdConfig.value.ActiveCallLimit)
const ActiveCallLimitColor = computed(() => thresholdConfig.value.ActiveCallLimitColor)
const ActiveCallWarning = computed(() => thresholdConfig.value.ActiveCallWarning)
const ActiveCallWarningColor = computed(() => thresholdConfig.value.ActiveCallWarningColor)
const HoldTimeLimit = computed(() => thresholdConfig.value.HoldTimeLimit)
const HoldTimeWarning = computed(() => thresholdConfig.value.HoldTimeWarning)
const HoldTimeLimitColor = computed(() => thresholdConfig.value.HoldTimeLimitColor)
const HoldTimeWarningColor = computed(() => thresholdConfig.value.HoldTimeWarningColor)

const pauseRecording = computed(() => {
    return props.call.recording.Filename && props.call.recording.IsMuted
})

const showPauseRecTitle = computed(() => {
    const isCardsColsConditionMet = props.cardsCols <= 3 && callInfoFontSize.value <= 20
    const isCallerNameConditionMet = props.cardsCols < 5 && (hasCallerName.value ? props.call.callername.length < 10 : true)
    const isTagsConditionMet = tagsContent.value.length < 2 || !hasCallerName.value
    const isFontSizeConditionMet = callInfoFontSize.value <= 17
    return isCardsColsConditionMet || (isCallerNameConditionMet && isTagsConditionMet && isFontSizeConditionMet)
})

const holdTagColor = computed(() => {
    return timerHoldState.value >= HoldTimeLimit.value
        ? HoldTimeLimitColor.value
        : timerHoldState.value >= HoldTimeWarning.value
            ? HoldTimeWarningColor.value
            : ''
})

const tagsContent = computed(() => {
    const call = {
        color: callTagColor.value,
        animated: !!callTagColor.value && !isVisualAlertDisabled.value,
        tagContent: [
            {
                iconName: callTagIcon.value,
                text: timerDisplayTime.value
            }
        ]
    }
    const hold = {
        color: holdTagColor.value,
        animated: !!callTagColor.value && !isVisualAlertDisabled.value,
        tagContent: [
            {
                iconName: 'Pause',
                text: timerHoldTimeDisplay.value
            }
        ]
    }
    return onHold.value ? [ call, hold ] : [ call ]
})
const hasCallerName = computed(() => {
    return props.call.callerphone !== props.call.callername
})
const shouldAnimate = computed(() => {
    return props.cardsCols >= 5 && hasCallerName.value && props.call.callerphone && props.call.callername
})

const soundHoldTimeLimit = computed(() => {
    return getSoundFromActiveLayout('SoundHoldTimeLimit')
})

const soundHoldTimeWarning = computed(() => {
    return getSoundFromActiveLayout('SoundHoldTimeWarning')
})

const soundActiveCallWarning = computed(() => {
    return getSoundFromActiveLayout('SoundActiveCallWarning')
})

const soundActiveCallLimit = computed(() => {
    return getSoundFromActiveLayout('SoundActiveCallLimit')
})


/*Methods*/
const holdStartTime = computed(() => {
    return props.call.holdStarted_UTC_CLIENT || new Date().getTime()
})

const soundObject = ref({
    infoHold: {
        sound: soundHoldTimeLimit.value,
        property: 'sound hold time limit',
        type: 'hold info',
        notification_value: timeFormatter(HoldTimeLimit.value)
    },
    warningHold: {
        sound: soundHoldTimeWarning.value,
        property: 'sound hold time warning',
        type: 'hold warning',
        notification_value: timeFormatter(HoldTimeWarning.value)
    },
    info: {
        sound: soundActiveCallLimit.value,
        property: 'sound active call limit',
        type: 'info',
        notification_value: timeFormatter(ActiveCallLimit.value)
    },
    warning: {
        sound: soundActiveCallWarning.value,
        property: 'sound active call warning',
        type: 'warning',
        notification_value: timeFormatter(ActiveCallWarning.value)
    }
})


const setupNotification = (status, currentTime, soundPlayed = false, id) => {
    const notificationData = soundObject.value[status]

    notificationData.current_value = currentTime
    notificationData.caller = props.call.callerphone
    notificationData.ivrid = props.call.ivrid

    const soundItem = {
        id: id,
        sound: notificationData.sound,
        played: soundPlayed,
        isPlaying: false,
        timestamp: new Date().getTime(),
        notificationHTML: i18n.t('widget.callInfoCardHtml', { ...notificationData })
    }

    if (soundItem.sound) {
        setupSoundQueue(soundItem)
    }

}

/*Watch*/
watch(() => props.call.callstatus, (val, oldValue) => {
    if (val === 'Hold') {
        startHoldTimer(holdStartTime)
        onHold.value = true

    } else if (oldValue === 'Hold' && val !== oldValue) {
        stopHoldTimer()
        onHold.value = false
    }
}, { immediate: true })

watch(() => timerState.value, (value) => {
    let newColor = ''
    let newIcon = ''
    let newActiveCallStatus = ''

    if (Number(ActiveCallWarning.value) && value >= Number(ActiveCallWarning.value)) {
        newColor = ActiveCallWarningColor.value
        newActiveCallStatus = CALL_STATUS.WARNING
    }
    if (Number(ActiveCallLimit.value) && value >= Number(ActiveCallLimit.value)) {
        newColor = ActiveCallLimitColor.value
        newActiveCallStatus = CALL_STATUS.INFO
    }

    if (newColor) {
        newIcon = TIMER_ICON
    }

    if (newIcon && newIcon !== callTagIcon.value) {
        callTagIcon.value = newIcon
    }

    if (newColor && newColor !== callTagColor.value) {
        callTagColor.value = newColor
    }

    if (newActiveCallStatus === activeCallStatus.value) {
        return
    }

    const id = `${props.call.ivrid}-${props.call.callerphone}-active-call`

    if (!newActiveCallStatus && activeCallStatus.value) {
        removeSoundsFromQueueByIdWithHistoryKeep(id)
        activeCallStatus.value = newActiveCallStatus

        return
    }


    if(newActiveCallStatus && activeCallStatus.value !== newActiveCallStatus) {
        removeSoundsFromQueueByIdWithHistoryKeep(id)

        const soundPlayed = activeCallStatus.value === CALL_STATUS.INFO && newActiveCallStatus === CALL_STATUS.WARNING
        const currentValue = timerDisplayTime.value

        activeCallStatus.value = newActiveCallStatus

        setupNotification(newActiveCallStatus, currentValue, soundPlayed, id)
    }

})

onBeforeUnmount(() => {
    removeSoundsFromQueueByIdWithHistoryKeep(`${props.call.ivrid}-${props.call.callerphone}-active-call`)
})

watch(() => timerHoldState.value, (value,) => {
    let newHoldStatus = ''

    if (HoldTimeWarning.value && value >= Number(HoldTimeWarning.value)) {
        newHoldStatus = CALL_HOLD_STATUS.WARNING
    }
    if (HoldTimeLimit.value && value >= Number(HoldTimeLimit.value)) {
        newHoldStatus = CALL_HOLD_STATUS.INFO
    }

    if (newHoldStatus === holdStatus.value) {
        return
    }

    const id = `${props.call.ivrid}-${props.call.callerphone}-hold`

    if (!newHoldStatus && holdStatus.value) {
        holdStatus.value = newHoldStatus
        removeSoundsFromQueueByIdWithHistoryKeep(id)

        return

    }
    if(newHoldStatus && holdStatus.value !== newHoldStatus) {
        removeSoundsFromQueueByIdWithHistoryKeep(id)

        const soundPlayed = holdStatus.value === CALL_HOLD_STATUS.INFO && newHoldStatus === CALL_HOLD_STATUS.WARNING

        holdStatus.value = newHoldStatus

        setupNotification(newHoldStatus, timerHoldTimeDisplay.value, soundPlayed, id)
    }
})

startTimer(callStartedTime)
</script>


<style lang="scss">
.el-tooltip__popper.is-customized.custom {
  @apply p-4;
  position: relative;
  background: var(--mainBgColor-color);
  color: var(--mainTextColor-color);
  border-width: 0;
  box-shadow: 0 8px 16px -2px rgba(27, 33, 44, 0.12);
  &::after {
    position: absolute;
    content: '\23F7';
    color: var(--mainBgColor-color);
    font-size: 18px;
    left: 50%;
    transform: translateX(-50%);
    bottom: -11px;
  }
}

.call-info {
    &-callerphone {
        @apply font-medium;
    }

    &-callername {
        @apply text-gray-600;
    }

    &.animated {
        .call-info-callerphone {
            animation: fadeAnimationOdd infinite 4s;
        }

        .call-info-callername {
            animation: fadeAnimationEven infinite 4s;

            @apply absolute left-0 top-0;
        }
    }
}

@keyframes fadeAnimationEven {
    0% {
        opacity: 1;
    }
    45% {
        opacity: 1;
    }
    50% {
        opacity: 0;
    }
    95% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
}

@keyframes fadeAnimationOdd {
    0% {
        opacity: 0;
    }
    45% {
        opacity: 0;
    }
    50% {
        opacity: 1;
    }
    95% {
        opacity: 1;
    }
    100% {
        opacity: 0;
    }
}
</style>
